(function () {
  if (window.UqoraPriceTest) return;

  const CONFIG_ELEMENT_ID = "price-test-config";
  const CART_ATTRIBUTE = "_price_test";
  const DEFAULT_CART_ATTRIBUTE_VALUE = "B";
  const PAGE_ATTRIBUTE = "data-uq-price-test";
  // Legacy buy boxes only; the unified buy box (assets/uq-buybox.js) computes
  // its savings from the discounted price. Values follow the client's
  // 2026-09-24 subscription-only plan, except Defend & Promote (plan: 37%) and
  // Flush & Defend (plan: 31%), which carry the savings the new $2 / $5 amounts
  // actually produce against compare-at ($39.99 vs $60.00, $36.99 vs $54.98).
  const SAVINGS_PERCENTAGES = {
    "complete-regimen": { subscription: 41 },
    "flush-advanced-regimen": { subscription: 40 },
    "defend-promote": { subscription: 33 },
    "flush-defend": { subscription: 33 },
    "flush-advanced-defend": { subscription: 33 },
    "flush-advanced": { subscription: 17 },
    defend: { "1-month": 17, "3-month": 22 },
    promote: { "1-month": 17, "3-month": 22 },
    flush: { "1-month": 20, "3-month": 25 },
  };

  let _config = null;
  try {
    const configEl = document.getElementById(CONFIG_ELEMENT_ID);
    if (configEl && configEl.textContent) {
      const parsed = JSON.parse(configEl.textContent);
      if (parsed && typeof parsed === "object" && parsed.variants) {
        _config = parsed;
      }
    }
  } catch (error) {
    console.warn("price-test: failed to parse config", error);
  }

  // Expected `_price_test` cart attribute value. Mirrors the discount
  // Function's config.flagValue override (defaults to "B" when absent or
  // empty) so display and discounting stay in lockstep during a pilot that
  // gates on a non-default token.
  const CART_ATTRIBUTE_VALUE =
    (_config && typeof _config.flagValue === "string" && _config.flagValue !== ""
      ? _config.flagValue
      : DEFAULT_CART_ATTRIBUTE_VALUE);

  let _enabled = false;

  const getCartUrl = () => {
    const basePath =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";
    return `${basePath}cart.js`;
  };

  const checkCart = async () => {
    if (!_config) {
      _enabled = false;
      return _enabled;
    }

    try {
      const response = await fetch(getCartUrl(), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        _enabled = false;
        return _enabled;
      }
      const cart = await response.json();
      _enabled = cart?.attributes?.[CART_ATTRIBUTE] === CART_ATTRIBUTE_VALUE;
    } catch (error) {
      console.warn("price-test: cart check failed", error);
      _enabled = false;
    }

    return _enabled;
  };

  const enabled = () => _enabled;

  const normalizeVariantGid = (variantId) => {
    const idString = `${variantId}`;
    if (idString.indexOf("gid://") === 0) return idString;
    return `gid://shopify/ProductVariant/${idString}`;
  };

  // Returns the discount amount in CENTS for a variant/purchase-type
  // combination, or 0 when price-test is disabled, the config is missing,
  // or the variant has no configured amount for that key.
  const amountFor = (variantId, isSubscription) => {
    if (!_enabled || !_config || !_config.variants || !variantId) return 0;

    const entry = _config.variants[normalizeVariantGid(variantId)];
    if (!entry) return 0;

    const key = isSubscription ? "subscription" : "onetime";
    const raw = entry[key];
    if (raw === null || raw === undefined || raw === "") return 0;

    const numeric = Number(raw);
    if (Number.isNaN(numeric) || numeric <= 0) return 0;

    return Math.round(numeric * 100);
  };

  // Returns the client-approved PDP savings percentage for the treatment
  // experience, or null when the test is disabled or the product/cadence is
  // not in scope. Control visitors keep the theme's calculated percentage.
  const savingsPercentFor = (productHandle, cadence) => {
    if (!_enabled || !productHandle || !cadence) return null;

    const savingsByCadence = SAVINGS_PERCENTAGES[productHandle];
    if (!savingsByCadence) return null;

    const percentage = Number(savingsByCadence[cadence]);
    return Number.isFinite(percentage) && percentage > 0 ? percentage : null;
  };

  const EVENTS = {
    updated: "uqora:price-test-updated",
  };

  let resolveReady;
  const ready = new Promise((resolve) => {
    resolveReady = resolve;
  });

  let _refreshing = null;
  const refresh = () => {
    if (_refreshing) return _refreshing;

    _refreshing = checkCart()
      .catch(() => {
        _enabled = false;
      })
      .finally(() => {
        _refreshing = null;
        // Treatment-only CSS hook (snippets/price-test-config.liquid) that hides
        // third-party prices the theme cannot rewrite: quiz results, the Smart
        // Cart empty-state recommendations and Rebuy upsell popups.
        document.documentElement.toggleAttribute(PAGE_ATTRIBUTE, _enabled);
        resolveReady();
        window.dispatchEvent(
          new CustomEvent(EVENTS.updated, { detail: { enabled: _enabled } })
        );
      });

    return _refreshing;
  };

  window.UqoraPriceTest = {
    events: EVENTS,
    ready,
    enabled,
    amountFor,
    savingsPercentFor,
    refresh,
  };

  refresh();

  // Cheap re-checks: bfcache restores don't re-run this script, and the
  // theme's cart pub/sub (see assets/pubsub.js) fires on every cart mutation
  // theme-wide, so both are good low-cost triggers to re-verify the
  // _price_test attribute without polling.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) refresh();
  });

  if (typeof subscribe === "function") {
    // PUB_SUB_EVENTS (assets/constants.js) is declared with `const` at top
    // level, so it never lands on `window` — reference it as a bare
    // identifier and fall back to the literal event name if it's undefined.
    const cartUpdateEvent =
      (typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) ||
      "cart-update";
    subscribe(cartUpdateEvent, refresh);
  }

  // Client ask (Oct 2026): the cart shows the test price without naming the
  // test discount. Rebuy renders one discount line per discount title under
  // each item; hide the price-test title and keep the others (TRYUQORA, sale
  // codes). Checkout still lists it, which the client accepted. The /cart page
  // and native drawer skip the same title in Liquid. Vue reuses these nodes,
  // so every mutation re-derives the flag from the current text.
  const DISCOUNT_TITLE = (_config && _config.discountMessage) || "";
  const DISCOUNT_LINE = ".rebuy-cart__flyout-item-discount-line";
  const DISCOUNT_MESSAGE = ".rebuy-cart__flyout-item-discount-message";
  const HIDDEN_ATTRIBUTE = "data-uq-price-test-hidden";

  const syncDiscountLabels = (root) => {
    root.querySelectorAll(DISCOUNT_LINE).forEach((line) => {
      line.toggleAttribute(HIDDEN_ATTRIBUTE, line.textContent.trim() === DISCOUNT_TITLE);
    });
    root.querySelectorAll(DISCOUNT_MESSAGE).forEach((message) => {
      message.toggleAttribute(
        HIDDEN_ATTRIBUTE,
        !message.querySelector(`${DISCOUNT_LINE}:not([${HIDDEN_ATTRIBUTE}])`)
      );
    });
  };

  const watchDiscountLabels = (cartRoot) => {
    syncDiscountLabels(cartRoot);
    // Attribute changes are not observed, so toggling the flag cannot loop.
    new MutationObserver(() => syncDiscountLabels(cartRoot)).observe(cartRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  if (DISCOUNT_TITLE) {
    const cartRoot = document.getElementById("rebuy-cart");
    if (cartRoot) {
      watchDiscountLabels(cartRoot);
    } else {
      // Rebuy mounts the Smart Cart after this deferred script runs.
      const mountObserver = new MutationObserver(() => {
        const mounted = document.getElementById("rebuy-cart");
        if (!mounted) return;
        mountObserver.disconnect();
        watchDiscountLabels(mounted);
      });
      mountObserver.observe(document.body, { childList: true, subtree: true });
    }
  }
})();
